// Enhanced RffDynamicForm.js
import {
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  ExpandMore as ExpandMoreIcon,
  Fullscreen,
  FullscreenExit,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Divider,
  IconButton,
  TextareaAutosize,
  Tooltip,
} from "@mui/material";
import { blue, blueGrey, grey } from "@mui/material/colors";
import { isArray } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import InputWrapper from "../inputs/shared/InputWrapper";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";
import RffForm from "./RffForm";
import RffFieldComponentRenderer from "./shared/RffFieldComponentRenderer";
import RffGroup from "./shared/RffGroup";
import RffFileUploadField from "./inputs/RffFileUploadField";

const RffDynamicForm = ({
  onSubmit,
  initialValues,
  loading,
  fields = [],
  formSpy,
  submitText,
  showFieldsEditor = true,
  prePopulateFieldsEditor = false,
}) => {
  const [fieldsInput, setFieldsInput] = useState([]);
  const [currentFields, setCurrentFields] = useState([]);
  const [error, setError] = useState("");
  const [modified, setModified] = useState(false);

  const handleFieldsUpdate = () => {
    try {
      const newFields = eval(`(${fieldsInput})`);
      const fieldsArray = Array.isArray(newFields) ? newFields : [newFields];

      const uniqueFields = fieldsArray
        .filter((field) => field && field.name)
        .filter(
          (field, index, self) =>
            index === self.findIndex((f) => f.name === field.name)
        );

      if (uniqueFields.length === 0) {
        throw new Error("No valid fields found");
      }

      setCurrentFields(uniqueFields);
      setError("");
      setModified(true);
    } catch (err) {
      setError(`Could not parse input: ${err.message}`);
    }
  };

  const resetToDefault = () => {
    if (isArray(fields)) {
      setFieldsInput(JSON.stringify(fields, null, 2));
    }
    setCurrentFields(fields);
    setModified(false);
    setError("");
  };

  const onClearAllClick = () => {
    setCurrentFields([]);
    setFieldsInput("");
    setModified(true);
    setError("");
  };

  useEffect(() => {
    if (prePopulateFieldsEditor && isArray(fields)) {
      setFieldsInput(JSON.stringify(fields, null, 2));
    }
    setCurrentFields(fields);
  }, [fields, prePopulateFieldsEditor]);

  return (
    <div>
      {showFieldsEditor && (
        <CopyAndPasteBox
          onResetClick={resetToDefault}
          currentFields={currentFields}
          error={error}
          onClearAllClick={onClearAllClick}
          fieldsInput={fieldsInput}
          setFieldsInput={setFieldsInput}
          onUpdate={handleFieldsUpdate}
          modified={modified}
        />
      )}

      <RffForm
        formSpy={formSpy}
        submitText={submitText}
        loading={loading}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        <RffGroup>
          {currentFields.map((field) => (
            <NestedFieldRenderer key={field.name} field={field} />
          ))}
          <RffFileUploadField
            label="Upload Photo Identification"
            docGroup={"Borrower Documents"}
            docType={"Photo Identification"}
            required
            helperText="Please ensure to upload both front and back of identification"
          />
        </RffGroup>
      </RffForm>
    </div>
  );
};

// Enhanced field renderer that handles nested objects and arrays
const NestedFieldRenderer = ({ field, parentPath = "" }) => {
  const fieldPath = parentPath ? `${parentPath}.${field.name}` : field.name;

  // Handle nested objects
  if (
    field.kind === "object" &&
    field.nested_model_schema &&
    !isArray(field.default)
  ) {
    return (
      <NestedItemWrapper
        label={field.label || field.name}
        sx={{ mb: 2, p: 2, backgroundColor: grey[50] }}
      >
        <Accordion defaultExpanded>
          <AccordionSummary
            sx={{ borderBottom: `1px solid #cccccc` }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Htag variant="h4" sx={{ fontWeight: 500 }}>
              {field.label || field.name} Details
            </Htag>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 3, px: 3 }}>
            <RffGroup suppressBottomMargin>
              {field.nested_model_schema.map((nestedField) => (
                <NestedFieldRenderer
                  key={nestedField.name}
                  field={nestedField}
                  parentPath={fieldPath}
                />
              ))}
            </RffGroup>
          </AccordionDetails>
        </Accordion>
      </NestedItemWrapper>
    );
  }

  // Handle arrays of objects
  if (
    field.kind === "object" &&
    field.nested_model_schema &&
    isArray(field.default)
  ) {
    return (
      <NestedItemWrapper label={field.label || field.name}>
        <FieldArray name={fieldPath}>
          {({ fields }) => (
            <div>
              {fields.map((name, index) => (
                <Card
                  key={name}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "white",
                    border: `1px solid ${grey[300]}`,
                  }}
                >
                  <Flx jb ac sx={{ mb: 2 }}>
                    <Htag variant="h4" sx={{ fontWeight: 500 }}>
                      {field.label || field.name} #{index + 1}
                    </Htag>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => fields.remove(index)}
                      // disabled={fields.length <= 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Flx>
                  <RffGroup suppressBottomMargin>
                    {field.nested_model_schema.map((nestedField) => (
                      <NestedFieldRenderer
                        key={`${name}.${nestedField.name}`}
                        field={nestedField}
                        parentPath={name}
                      />
                    ))}
                  </RffGroup>
                </Card>
              ))}
              <Flx center fw>
                <Button
                  variant="outlined"
                  color={"inherit"}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Create default object based on nested schema
                    const defaultObj = {};
                    field.nested_model_schema.forEach((nestedField) => {
                      defaultObj[nestedField.name] =
                        nestedField.default !== undefined
                          ? nestedField.default
                          : null;
                    });
                    fields.push(defaultObj);
                  }}
                  sx={{ margin: "0 auto" }}
                >
                  Add {field.label || field.name}
                </Button>
              </Flx>
            </div>
          )}
        </FieldArray>
      </NestedItemWrapper>
    );
  }

  // Handle regular scalar fields
  return <EnhancedRffFieldComponentRenderer field={field} name={fieldPath} />;
};

// Enhanced field component renderer that accepts custom name prop
const EnhancedRffFieldComponentRenderer = ({ field, name, ...props }) => {
  const fieldName = name || field.name;

  // If your existing RffFieldComponentRenderer doesn't accept a name prop,
  // you'll need to modify it or create a wrapper like this:
  return (
    <Field name={fieldName}>
      {(fieldProps) => (
        <RffFieldComponentRenderer
          field={{ ...field, name: fieldName }}
          {...fieldProps}
          {...props}
        />
      )}
    </Field>
  );
};

const NestedItemWrapper = ({ label, children }) => {
  return (
    <RffGroup disableGrid isGridChild suppressBottomMargin>
      <InputWrapper label={label}>
        <Card sx={{ mb: 2, p: 2, backgroundColor: blueGrey[50] }}>
          {children}
        </Card>
      </InputWrapper>
    </RffGroup>
  );
};

const CopyAndPasteBox = ({
  fieldsInput,
  setFieldsInput,
  error,
  onUpdate,
  onResetClick,
  onClearAllClick,
  modified,
  currentFields,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const styles = useMemo(() => {
    let sty = {
      marginBottom: "20px",
      padding: "15px",
      mb: 4,
      borderRadius: "5px",
      height: "100%",
      backgroundColor: blue[50],
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      zIndex: 11111,
    };
    if (fullScreen) {
      sty = {
        ...sty,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
      };
    }
    return sty;
  }, [fullScreen]);
  return (
    <>
      <Card sx={styles}>
        <Flx fw jb ac>
          <Flx column>
            <Txt bold>Form Question Schema</Txt>
            <Txt secondary>
              Copy & paste an array of form fields, or modify the existing
              fields below.
            </Txt>
          </Flx>
          <Flx ac gap={0}>
            {!collapsed && (
              <Tooltip
                title={fullScreen ? "Exit Full Screen" : "Enter Full Screen"}
              >
                <IconButton
                  sx={{ background: fullScreen ? blue[100] : "initial" }}
                  onClick={() => setFullScreen((s) => !s)}
                >
                  {fullScreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            )}
            {!fullScreen && (
              <IconButton onClick={() => setCollapsed((s) => !s)}>
                {collapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            )}
          </Flx>
        </Flx>
        {collapsed ? null : (
          <>
            <TextareaAutosize
              error={error}
              value={fieldsInput}
              placeholder="Paste your fields array here (JSON format)..."
              onChange={(e) => setFieldsInput(e?.target.value)}
              multiline
              minRows={1}
              style={{
                marginTop: "8px",
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: "4px",
                padding: "10px",
                width: "100%",
                maxWidth: "100%",
                minWidth: "100%",
                minHeight: "100px",
                fontSize: "11px",
                maxHeight: fullScreen ? "100%" : "500px",
                height: "200px",
              }}
              maxRows={6}
              size="medium"
            />
            {error && (
              <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                {error}
              </div>
            )}
            <Flx fw jb ac sx={{ mt: 2 }}>
              <Txt secondary>Total Fields: {currentFields.length}</Txt>
              <Flx end gap={1}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onClearAllClick}
                >
                  Clear All Fields
                </Button>
                {modified && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onResetClick}
                  >
                    Reset To Default
                  </Button>
                )}
                <Button variant="outlined" onClick={onUpdate}>
                  Update Fields
                </Button>
              </Flx>
            </Flx>
          </>
        )}
      </Card>
      <Divider
        sx={{
          my: 6,
          borderStyle: "dashed",
          borderWidth: "2px",
          borderTop: "none",
        }}
      />
    </>
  );
};

export default RffDynamicForm;
